import "dayjs/locale/uk";

import { UserSwitchOutlined } from "@ant-design/icons";
import { App, ConfigProvider, DatePicker, Flex } from "antd";
import ukUA from "antd/locale/uk_UA";
import dayjs, { Dayjs } from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getApiError, getTaskList } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { AnyTask, Point, TaskRoute } from "tm-shared/types";

import { TaskCard, TaskViewModalsContainer } from "@/features/TaskView";
import { useAppDispatch, useOfflineModeSelector } from "@/hooks/redux";
import BaseButton from "@/shared/ui/BaseButton";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { TasksTabCapabilities } from "../AboutPoint.policy";

interface TasksTabProps {
    point: Point;
    capabilities: TasksTabCapabilities;
}

const QUERY_DATE = "date";
const QUERY_TASK_ID = "taskId";

export const TasksTab: FC<TasksTabProps> = ({ point, capabilities }) => {
    const { notification } = App.useApp();
    const offlineMode = useOfflineModeSelector();

    const dispatch = useAppDispatch();
    const today = dayjs();

    const [searchParams, setSearchParams] = useSearchParams();

    const queryDateStr =
        searchParams.get(QUERY_DATE) ?? today.format(backendDateFormat.date);

    // date з query (або today)
    const targetDate = useMemo(
        () => dayjs(queryDateStr, backendDateFormat.date, true),
        [queryDateStr],
    );

    const modalTaskId = searchParams.get(QUERY_TASK_ID);

    const [tasks, setTasks] = useState<AnyTask[]>([]);
    const [tasksLoading, setTasksLoading] = useState(false);

    // Відкрита модалка як derived state: task з списку по taskId
    const modalTask = useMemo(() => {
        if (!modalTaskId) return null;
        return tasks.find((t) => t.id === modalTaskId) ?? null;
    }, [tasks, modalTaskId]);

    const createCustomTaskForAgent = useCallback(async () => {
        dispatch(
            addModalToQueue({
                type: ModalType.CreateCustomTask,
                id: ModalType.CreateCustomTask,
                payload: {
                    point,
                    images: [],
                },
            }),
        );
    }, [dispatch, point]);

    // Фетч задач при зміні point/role/date
    useEffect(() => {
        const fetchTasks = async () => {
            try {
                setTasksLoading(true);
                setTasks([]);

                const { result: serverTasks } = await getTaskList({
                    targetId: point.id,
                    roleSlug: capabilities.taskAccessRole ?? undefined,
                    targetDate: queryDateStr,
                });

                const sortedTasks = [...serverTasks].sort((a, b) => {
                    const aIsStart = a.context.route === TaskRoute.StartVisit;
                    const bIsStart = b.context.route === TaskRoute.StartVisit;

                    if (aIsStart !== bIsStart) return aIsStart ? -1 : 1;

                    return a.name.localeCompare(b.name, "uk", {
                        sensitivity: "base",
                    });
                });

                setTasks(sortedTasks);
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося завантажити задачі",
                    description: displayMessage,
                });
            } finally {
                setTasksLoading(false);
            }
        };

        fetchTasks();
    }, [capabilities.taskAccessRole, point, notification, queryDateStr]);

    // Якщо в query є taskId, але після завантаження задач його нема в списку — прибираємо з query
    useEffect(() => {
        if (!modalTaskId) return;
        if (tasksLoading) return;
        if (tasks.length === 0) return;

        const exists = tasks.some((t) => t.id === modalTaskId);
        if (!exists) {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.delete(QUERY_TASK_ID);
                    return sp;
                },
                { replace: true },
            );
        }
    }, [modalTaskId, tasks, tasksLoading, setSearchParams]);

    const setQueryDate = useCallback(
        (d: Dayjs) => {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.set(QUERY_DATE, d.format(backendDateFormat.date));
                    return sp;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const handleChangeDate = useCallback(
        (value: Dayjs | null) => {
            if (!value) return;
            setQueryDate(value);
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.delete(QUERY_TASK_ID);
                    sp.set(QUERY_DATE, value.format(backendDateFormat.date));
                    return sp;
                },
                { replace: true },
            );
        },
        [setQueryDate, setSearchParams],
    );

    const handleOpenModal = useCallback(
        (taskId: string) => {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.set(QUERY_TASK_ID, taskId);
                    sp.set(
                        QUERY_DATE,
                        targetDate.format(backendDateFormat.date),
                    );
                    return sp;
                },
                { replace: true },
            );
        },
        [setSearchParams, targetDate],
    );

    const handleCloseModal = useCallback(() => {
        setSearchParams(
            (prev) => {
                const sp = new URLSearchParams(prev);
                sp.delete(QUERY_TASK_ID);
                return sp;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return (
        <>
            <Flex vertical gap={16} flex={1}>
                <ConfigProvider locale={ukUA}>
                    <DatePicker
                        size="large"
                        format="DD MMMM YYYYр."
                        placeholder="Оберіть дату"
                        style={{ width: "100%" }}
                        maxDate={today}
                        value={targetDate}
                        onChange={handleChangeDate}
                        allowClear={false}
                        disabled={offlineMode || tasksLoading}
                    />
                </ConfigProvider>
                {capabilities.canCreateTask && (
                    <BaseButton
                        icon={<UserSwitchOutlined />}
                        onClick={createCustomTaskForAgent}
                    >
                        Створити доручення
                    </BaseButton>
                )}

                {tasksLoading && <BlockLoader />}

                {!tasksLoading && tasks.length === 0 && (
                    <PrimaryText>За даною датою задач не знайдено</PrimaryText>
                )}

                {!tasksLoading && tasks.length > 0 && (
                    <Flex vertical gap={8}>
                        {tasks.map((t) => (
                            <TaskCard
                                key={t.id}
                                task={t}
                                onOpenModal={() => handleOpenModal(t.id)}
                            />
                        ))}
                    </Flex>
                )}
            </Flex>

            <TaskViewModalsContainer
                modal={
                    modalTask
                        ? {
                              task: modalTask,
                              point,
                          }
                        : null
                }
                onClose={handleCloseModal}
            />
        </>
    );
};
